/**
 * `/app/widgets/$widgetRef` — one widget's page.
 *
 * **One route for every widget type.** `client/widget-chatbot.html` and
 * `client/widget-booking.html` are two mockups of one screen: what differs is the
 * unit noun, the numbers and the prose, and every one of those comes from the
 * widget's own row (`.docs/13` — "adding a widget type" is a `widget_type` row and
 * no code). The nav items are dynamic for the same reason (`shell/nav.ts`).
 *
 * **An index, and not a leaf** (session 46). A hosted widget's workspace hangs under
 * the widget's own address — `…/submissions`, `…/forms`, `…/posts` — and a leaf
 * `$widgetRef.tsx` would be the *parent* of every one of them, rendering this page
 * with no outlet for theirs. As `$widgetRef/index.tsx` it is their sibling, exactly
 * as `/app/reports` is `$period`'s; a `Link to="/app/widgets/$widgetRef"` is
 * unchanged.
 */

import { createFileRoute } from '@tanstack/react-router'

import { NotFound } from '../../../../errors/NotFound.js'
import { WidgetPage, parseWidgetSearch } from '../../../../pages/client/widget/WidgetPage.js'

export const Route = createFileRoute('/app/widgets/$widgetRef/')({
  /**
   * `?period=` is this route's own, so it is declared here: a search parameter a
   * route does not declare is one the router is free to drop across a navigation,
   * and the page writes this one on every click of its segmented control.
   */
  validateSearch: parseWidgetSearch,
  /**
   * R3-13, and here it is a promise rather than a repair: this route refuses no
   * `$widgetRef` today, so nothing reaches the boundary. The day it does — a ref
   * that could name no widget is a not-found, exactly as `$period` and `$slug`
   * already are — the refusal has to keep the portal, and `notFound.test.tsx`
   * requires every parameterised route to have said so before that day.
   */
  notFoundComponent: NotFound,
  component: WidgetRoute,
})

function WidgetRoute() {
  const { widgetRef } = Route.useParams()
  return <WidgetPage widgetRef={widgetRef} />
}
